import {FC, useEffect, useState} from 'react';
//mui
import {Box, Drawer, Tooltip, Typography, useMediaQuery} from '@mui/material';
//formik
import {Field, useFormikContext} from 'formik';
//constants
import {
  CANCEL,
  COLLECT_PAYMENT,
  FIELD,
  FIELD_LABEL,
  H5,
  HASH,
  NONE,
  NUMBER,
  PLACEHOLDER,
  PURCHASER,
  SAVE_CHANGES,
  SUBTITLE1,
  TABLE,
  TABLE_PURCHASER,
  TOP,
  mealsSelectOptions,
} from '@/utils/constants';
//components
import Input from '@/shared/components/Inputs/Input';
import MultipleSelect from '@/shared/components/MultipleSelect';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import TableFooterActions from '@/shared/components/Table/TableFooterActions';
import TableFields from './TableFields';
import Button from '@/shared/components/Button';
import Modal from '@/shared/components/Modal';
import AttendeeDetails from '@/components/Organization/Attendees/AttendeeDetails';
//helpers
import {getErrorMessage, setRequiredLabel} from '@/utils/helpers';
//types
import {IOrganizationTableTicketsResponse} from '@/utils/types';
//styles
import {formContentStyle} from './FormContent.styles';
import {ReactComponent as QuestionIcon} from '@/assets/icons/modal-question.svg';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import {attendeesTableStyle} from '@/components/Organization/Attendees/AttendeesTable/AttendeesTable.styles';
import {attendeeDetailsTabs} from '@/components/Organization/Attendees/AttendeeDetails/helpers';
import {useNavigate} from 'react-router-dom';
import {ROUTER_URL} from '@/utils/enums';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import {toast} from 'react-toastify';

interface FormContentProps {
  handleCloseModal?: () => void;
}

const FormContent: FC<FormContentProps> = ({handleCloseModal}) => {
  const {
    submitForm,
    values: {name, number, purchaser, tickets, tableInfo, isPaid, purchaserId},
  } = useFormikContext<IOrganizationTableTicketsResponse>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const attendee = useAppSelector(getAttendeeSelector);

  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);
  const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
  const [tableTitle, setTableTitle] = useState(name);

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
    if (!attendeeId) return;

    setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

    if (!isShowAttendeeDetailsModal && attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

  useEffect(() => {
    if (!tableTitle) {
      setTableTitle(name);
    }
  }, [name]);

  const handleCollectPayment = () => {
    isLaptop
      ? handleChangeAttendeeDetailsModal(purchaserId)
      : navigate(`${ROUTER_URL.ORGANIZATION_ATTENDEES}/${purchaserId}`, {
          state: {activeTab: attendeeDetailsTabs[2], isTabsVisible: false},
        });
  };

  return (
    <Box sx={formContentStyle.wrapper}>
      <Typography variant={H5} sx={formContentStyle.header}>
        {tableTitle}
      </Typography>

      <Box sx={formContentStyle.textWithTooltip}>
        <Typography variant={SUBTITLE1} sx={formContentStyle.title}>
          PAID: {isPaid ? `$${tableInfo?.price}` : '$0.00'}
        </Typography>
        {isPaid && (
          <Tooltip title={`Paid by: ${purchaser.firstName} ${purchaser.lastName}`} placement={TOP}>
            <Box sx={formContentStyle.coverFeeTooltip}>
              <QuestionIcon />
            </Box>
          </Tooltip>
        )}
      </Box>

      <Box sx={formContentStyle.content}>
        <Box sx={formContentStyle.ticket}>
          <Field
            id={FIELD.number}
            name={FIELD.number}
            label={`${TABLE}${HASH}`}
            component={Input}
            value={number}
            sx={formContentStyle.ticketNumber}
          />
          <Field
            id={FIELD.name}
            name={FIELD.name}
            placeholder={PLACEHOLDER.tableName}
            label={setRequiredLabel(FIELD_LABEL.tableName)}
            asd="true"
            component={Input}
            value={name}
            sx={formContentStyle.ticketName}
          />
          <Field
            id={`tableInfo.${FIELD.price}`}
            name={`tableInfo.${FIELD.price}`}
            placeholder={PLACEHOLDER.price}
            label={setRequiredLabel(FIELD_LABEL.ticketPrice)}
            component={Input}
            value={tableInfo.price}
            isObjectData
            sx={formContentStyle.ticketPrice}
            disabled={isPaid}
            type={NUMBER}
          />
        </Box>

        <Typography variant={SUBTITLE1} sx={formContentStyle.title}>
          {TABLE_PURCHASER}:
        </Typography>

        <Box sx={formContentStyle.purchaser}>
          <Field
            id={`${PURCHASER}.${FIELD.firstName}`}
            name={`${PURCHASER}.${FIELD.firstName}`}
            placeholder={PLACEHOLDER.firstName}
            label={setRequiredLabel(FIELD_LABEL.firstName)}
            value={purchaser?.firstName}
            component={Input}
            isObjectData
          />
          <Field
            id={`${PURCHASER}.${FIELD.lastName}`}
            name={`${PURCHASER}.${FIELD.lastName}`}
            placeholder={PLACEHOLDER.lastName}
            label={setRequiredLabel(FIELD_LABEL.lastName)}
            value={purchaser?.lastName}
            component={Input}
            isObjectData
          />
          <Field
            id={`${PURCHASER}.${FIELD.email}`}
            name={`${PURCHASER}.${FIELD.email}`}
            placeholder={PLACEHOLDER.emailEnter}
            label={setRequiredLabel(FIELD_LABEL.email)}
            value={purchaser?.email}
            component={Input}
            isObjectData
          />
          <Field
            id={`${PURCHASER}.${FIELD.phoneNumber}`}
            name={`${PURCHASER}.${FIELD.phoneNumber}`}
            placeholder={PLACEHOLDER.phoneNumberZero}
            label={FIELD_LABEL.phoneNumber}
            value={purchaser?.phoneNumber}
            component={Input}
            isObjectData
          />
          <Box sx={formContentStyle.meals}>
            <Field
              id={`${PURCHASER}.${FIELD.meals}`}
              name={`${PURCHASER}.${FIELD.meals}`}
              value={purchaser?.meals || []}
              defaultValue={NONE}
              label={FIELD_LABEL.meals}
              component={MultipleSelect}
              options={mealsSelectOptions}
            />
          </Box>
          <ActionsMenu
            disableRipple
            content={
              <Typography sx={formContentStyle.menuItem} onClick={handleCollectPayment}>
                {COLLECT_PAYMENT}
              </Typography>
            }
          />
        </Box>
      </Box>

      <TableFields />

      {!tickets.length && <EmptyTableData />}

      <TableFooterActions
        titleButtonPrev={CANCEL}
        titleButtonNext={SAVE_CHANGES}
        onClickButtonNext={submitForm}
        onClickButtonPrev={handleCloseModal}
      />

      <Drawer variant="permanent" anchor="bottom" sx={formContentStyle.drawer}>
        <Box sx={formContentStyle.popup}>
          <Button title="Save Changes" onClick={submitForm} />
        </Box>
      </Drawer>
      <Modal
        onClose={handleChangeAttendeeDetailsModal}
        open={isShowAttendeeDetailsModal && !!attendee}
        sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
      >
        <AttendeeDetails
          onCloseModal={handleChangeAttendeeDetailsModal}
          handleProfileTabStyles={handleProfileTabStyles}
          activeAttendeeTab={attendeeDetailsTabs[2]}
          isTabsVisible={false}
        />
      </Modal>
    </Box>
  );
};

export default FormContent;
